import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShopProductCard from './ShopProductCard';
import { addUnit, normalizeAppUrl, resolveMediaUrl, stripHtml, getFontStyles } from '../../utils/styleHelpers';
import { getProductImageUrl, getProductDisplayPrice, getVariationPriceDetails } from '../../utils/helpers';
import { useCurrency } from '../CurrencyState';

export const ShopProductGrid = ({
    loading = false,
    sortedProducts = [],
    catalogViewMode = 'grid',
    currentPage = 1,
    GRID_PAGE_SIZE = 12,
    handlePageChange,
    quickOrderPage = 1,
    QUICK_ORDER_PAGE_SIZE = 25,
    handleQuickOrderPageChange,
    tableQuantities = {},
    handleTableQtyChange,
    handleTableAddToCart,
    addBatchToCart,
    batchAddedMessage,
    setBatchAddedMessage,
    getSelectableAttributes,
    getEffectiveSelectedOptions,
    getMatchedVariation,
    handleOptionChange,
    tableSelectedOptions = {},
    expandedRowIds = {},
    toggleRowExpand,
    addedRowIds = {},
    quickOrderModalProduct,
    setQuickOrderModalProduct,
    formatPrice,
    isEditing = false,
    isMobile = false,
    isTablet = false,
    styleSettings = {},
    settings = {},
    typography = {},
    primaryColor = '#3b82f6',
    addToCart,
    updateProductField,
    handleDuplicateProductLive,
    handleDeleteProductLive,
    onEditProductDetails,
    update,
    _getFontStyles,
    dbCategories = [],
    activeCategory = 'All',
    activeSubCategory = 'All',
    setActiveCategory,
    setActiveSubCategory,
    isCommercialMode = false,
    isSimpleMode = false,
    selectedSimpleSection = null,
    onSectionSelect
}) => {
    const { formatPrice: contextFormatPrice } = useCurrency();
    const activeFormatPrice = formatPrice || contextFormatPrice;

    const resolveFontStyles = typeof _getFontStyles === 'function'
        ? _getFontStyles
        : getFontStyles(typography || settings?.typography || {});
    const callForPriceText = styleSettings.shopCallForPriceText || styleSettings.callForPriceText || settings?.shopCallForPriceText || 'Call for Price';
    const effectiveCommercialMode = Boolean(isCommercialMode || styleSettings?.shopCommercialOrderForm || settings?.shopCommercialOrderForm || settings?.cartCommercialMode);
    const hideCallForPrice = Boolean(styleSettings?.shopHideCallForPriceText || styleSettings?.shopCallForPriceHideText);
    const callForPriceFontSize = addUnit(styleSettings?.shopCallForPriceFontSize || styleSettings?.shopPriceFallbackFontSize || '0.78rem');

    const renderPagination = (safePage, totalPages, onPageChange, totalItems, pageSize, isTop = false) => {
        if (totalPages <= 1) return null;
        return (
            <div className={`shop-pagination-wrap ${isTop ? 'shop-pagination-top' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: '4px' }}>
                    Page {safePage} of {totalPages} ({totalItems} items)
                </span>
                <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => onPageChange(safePage - 1)}
                    style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: safePage <= 1 ? '#f8fafc' : '#ffffff',
                        color: safePage <= 1 ? '#94a3b8' : '#334155',
                        cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                    }}
                >
                    ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                    .map((p, idx, arr) => {
                        const prevPage = arr[idx - 1];
                        const showEllipsis = prevPage && p - prevPage > 1;
                        return (
                            <React.Fragment key={p}>
                                {showEllipsis && <span style={{ padding: '0 2px', color: '#94a3b8', fontSize: '0.75rem' }}>...</span>}
                                <button
                                    type="button"
                                    onClick={() => onPageChange(p)}
                                    style={{
                                        padding: '4px 9px',
                                        borderRadius: '6px',
                                        border: p === safePage ? `1px solid ${primaryColor}` : '1px solid #cbd5e1',
                                        background: p === safePage ? primaryColor : '#ffffff',
                                        color: p === safePage ? '#ffffff' : '#334155',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: p === safePage ? 'bold' : 'normal'
                                    }}
                                >
                                    {p}
                                </button>
                            </React.Fragment>
                        );
                    })}
                <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => onPageChange(safePage + 1)}
                    style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: safePage >= totalPages ? '#f8fafc' : '#ffffff',
                        color: safePage >= totalPages ? '#94a3b8' : '#334155',
                        cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                    }}
                >
                    Next ›
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '80px 0', fontSize: '1.2rem', color: '#64748b' }}>
                Loading products...
            </div>
        );
    }

    if (sortedProducts.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '80px 0', fontSize: '1.2rem', color: '#64748b' }}>
                No products found matching your criteria.
            </div>
        );
    }

    if (catalogViewMode === 'table') {
        const qoTotalPages = Math.max(1, Math.ceil(sortedProducts.length / QUICK_ORDER_PAGE_SIZE));
        const qoSafePage = Math.min(quickOrderPage, qoTotalPages);
        const qoStart = (qoSafePage - 1) * QUICK_ORDER_PAGE_SIZE;
        const qoPagedProducts = sortedProducts.slice(qoStart, qoStart + QUICK_ORDER_PAGE_SIZE);

        return (
            <div className="contractor-quick-order-wrapper" style={{ width: '100%', maxWidth: '100%', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '16px', boxSizing: 'border-box', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {/* Header Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', paddingBottom: '14px', marginBottom: '14px', borderBottom: '2px solid #e2e8f0' }}>
                    <div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span>📋 Contractor &amp; Wholesale Quick Order</span>
                            <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                {styleSettings.shopQuickOrderTableLabel || settings.shopQuickOrderTableLabel || 'Quick Order Table'}
                            </span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                            Select product options, enter item quantities, and click individual row order or batch order below.
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: 'auto', flexWrap: 'wrap' }}>
                        {batchAddedMessage && (
                            <span style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 'bold', background: '#dcfce7', padding: '6px 12px', borderRadius: '6px' }}>
                                ✓ {batchAddedMessage}
                            </span>
                        )}
                        {renderPagination(qoSafePage, qoTotalPages, handleQuickOrderPageChange, sortedProducts.length, QUICK_ORDER_PAGE_SIZE, true)}
                        <button
                            type="button"
                            onClick={() => {
                                const itemsToAdd = [];
                                sortedProducts.forEach(product => {
                                    const qty = tableQuantities[product.id] ?? 0;
                                    if (qty > 0) {
                                        const effectiveOptions = getEffectiveSelectedOptions(product);
                                        const hasOptions = Object.keys(effectiveOptions).length > 0;
                                        const matchedVar = getMatchedVariation(product, effectiveOptions);
                                        itemsToAdd.push({
                                            product,
                                            quantity: qty,
                                            selectedAttributes: hasOptions ? effectiveOptions : null,
                                            variation: matchedVar
                                        });
                                    }
                                });
                                if (itemsToAdd.length > 0) {
                                    if (addBatchToCart) addBatchToCart(itemsToAdd);
                                    if (setBatchAddedMessage) {
                                        setBatchAddedMessage(`Added ${itemsToAdd.length} product(s) to cart!`);
                                        setTimeout(() => setBatchAddedMessage(''), 3500);
                                    }
                                } else {
                                    alert('Set quantity > 0 for at least one item.');
                                }
                            }}
                            style={{
                                background: '#166534',
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px 14px',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                fontSize: '0.84rem',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px rgba(22, 101, 52, 0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span>🛒 Add Quantities &gt; 0 to Cart</span>
                        </button>
                    </div>
                </div>

                {/* Desktop & Tablet Table */}
                <div className="quick-order-desktop-table" style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem', tableLayout: 'auto' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                <th style={{ padding: '8px 2px', whiteSpace: 'nowrap', minWidth: '38px' }}>Item</th>
                                <th style={{ padding: '8px 4px', whiteSpace: 'nowrap', minWidth: '70px' }}>SKU</th>
                                <th style={{ padding: '8px 4px' }}>Product Title</th>
                                <th style={{ padding: '8px 4px', whiteSpace: 'nowrap', textAlign: 'center', minWidth: '42px' }}>Spec</th>
                                {!effectiveCommercialMode && <th style={{ padding: '8px 4px', whiteSpace: 'nowrap', minWidth: '80px' }}>Price</th>}
                                <th style={{ padding: '8px 4px', whiteSpace: 'nowrap', minWidth: '82px' }}>Qty</th>
                                <th style={{ padding: '8px 4px', whiteSpace: 'nowrap', textAlign: 'right', minWidth: '48px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {qoPagedProducts.map((product, idx) => {
                                const selectableAttrs = getSelectableAttributes ? getSelectableAttributes(product) : {};
                                const effectiveOptions = getEffectiveSelectedOptions ? getEffectiveSelectedOptions(product) : {};
                                const matchedVar = getMatchedVariation ? getMatchedVariation(product, effectiveOptions) : null;
                                const sku = matchedVar?.sku || product.sku || product.pageConfig?.sku || product.id || 'N/A';
                                const imageSrc = matchedVar?.image ? resolveMediaUrl(matchedVar.image) : getProductImageUrl(product);
                                const hasSelectableOptions = Object.keys(selectableAttrs).length > 0;
                                const isExpanded = !!expandedRowIds[product.id];

                                const varPriceInfo = getVariationPriceDetails(matchedVar, product);
                                const priceDisplay = (effectiveCommercialMode || (product.inquiryOnly && hideCallForPrice))
                                    ? null
                                    : (product.inquiryOnly
                                        ? <span style={{ color: styleSettings.shopCallForPriceColor || '#d97706', fontWeight: 'bold', fontSize: callForPriceFontSize }}>{callForPriceText}</span>
                                        : <span style={{ fontWeight: 'bold', color: '#0f172a' }}>
                                            {hasSelectableOptions && matchedVar ? (
                                                varPriceInfo.isOnSale && varPriceInfo.regularPrice ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px' }}>
                                                        <span>{activeFormatPrice ? activeFormatPrice(varPriceInfo.price) : `$${varPriceInfo.price.toFixed(2)}`}</span>
                                                        <span style={{ fontSize: '0.82em', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '500' }}>
                                                            {activeFormatPrice ? activeFormatPrice(varPriceInfo.regularPrice) : `$${varPriceInfo.regularPrice.toFixed(2)}`}
                                                        </span>
                                                    </span>
                                                ) : varPriceInfo.price > 0 ? (
                                                    activeFormatPrice ? activeFormatPrice(varPriceInfo.price) : `$${varPriceInfo.price.toFixed(2)}`
                                                ) : (
                                                    getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                                )
                                            ) : (
                                                getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                            )}
                                        </span>);
                                const specSheetUrl = product.specSheetUrl || product.pageConfig?.specSheetUrl;
                                const resolvedSpecUrl = specSheetUrl ? resolveMediaUrl(specSheetUrl) : null;
                                const currentQty = tableQuantities[product.id] ?? 0;
                                const isAdded = !!addedRowIds[product.id];

                                return (
                                    <React.Fragment key={product.id}>
                                        <tr
                                            style={{
                                                borderBottom: hasSelectableOptions && isExpanded ? 'none' : '1px solid #e2e8f0',
                                                background: isExpanded ? '#f0f9ff' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                                                transition: 'background 0.15s ease'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isExpanded ? '#e0f2fe' : '#f1f5f9'}
                                            onMouseLeave={e => e.currentTarget.style.background = isExpanded ? '#f0f9ff' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc')}
                                        >
                                            <td style={{ padding: '5px 2px', whiteSpace: 'nowrap' }}>
                                                <img
                                                    src={imageSrc}
                                                    alt={stripHtml(product.name)}
                                                    loading={idx < 4 ? "eager" : "lazy"}
                                                    decoding="async"
                                                    {...(idx < 4 ? { fetchpriority: "high" } : {})}
                                                    width="32"
                                                    height="32"
                                                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #cbd5e1', cursor: 'pointer', aspectRatio: '1/1', display: 'block' }}
                                                    onClick={() => navigate(normalizeAppUrl(`/product/${product.slug || product.id}`))}
                                                />
                                            </td>
                                            <td style={{ padding: '5px 4px', fontWeight: '700', color: '#334155', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                {sku}
                                            </td>
                                            <td style={{ padding: '5px 4px', whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                    <div
                                                        style={{ fontWeight: '700', color: '#0f172a', cursor: 'pointer', fontSize: '0.82rem', lineHeight: '1.2' }}
                                                        onClick={() => navigate(normalizeAppUrl(`/product/${product.slug || product.id}`))}
                                                    >
                                                        {stripHtml(product.name)}
                                                    </div>
                                                    {hasSelectableOptions && (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleRowExpand && toggleRowExpand(product.id)}
                                                            style={{
                                                                background: isExpanded ? '#3b82f6' : '#f1f5f9',
                                                                color: isExpanded ? '#ffffff' : '#475569',
                                                                border: isExpanded ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                                                borderRadius: '12px',
                                                                padding: '1px 7px',
                                                                fontSize: '0.68rem',
                                                                fontWeight: '700',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '3px',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                        >
                                                            <span>⚙️ Options ({Object.keys(selectableAttrs).length})</span>
                                                            <span>{isExpanded ? '▲' : '▼'}</span>
                                                        </button>
                                                    )}
                                                </div>
                                                {(() => {
                                                    let cats = [];
                                                    if (Array.isArray(product.categories)) {
                                                        cats = product.categories.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean);
                                                    } else if (typeof product.categories === 'string' && product.categories.trim()) {
                                                        try {
                                                            const p = JSON.parse(product.categories);
                                                            if (Array.isArray(p)) cats = p.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean);
                                                        } catch (e) {
                                                            cats = product.categories.split(',').map(s => s.trim()).filter(Boolean);
                                                        }
                                                    }
                                                    if (cats.length === 0 && product.pageConfig && Array.isArray(product.pageConfig.categories)) {
                                                        cats = product.pageConfig.categories.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean);
                                                    }
                                                    if (cats.length === 0 && product.category) {
                                                        cats = product.category.split(',').map(s => s.trim()).filter(Boolean);
                                                    }
                                                    const catLabel = cats.join(', ') || product.category;
                                                    if (!catLabel && !product.subCategory) return null;
                                                    return (
                                                        <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {catLabel ? stripHtml(catLabel) : ''} {product.subCategory ? `› ${stripHtml(product.subCategory)}` : ''}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ padding: '5px 2px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                {resolvedSpecUrl ? (
                                                    <a
                                                        href={resolvedSpecUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '2px 4px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '0.68rem', fontWeight: '600', textDecoration: 'none' }}
                                                    >
                                                        <span>📄</span>
                                                    </a>
                                                ) : (
                                                    <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>—</span>
                                                )}
                                            </td>
                                            {!effectiveCommercialMode && (
                                                <td style={{ padding: '5px 3px', whiteSpace: 'nowrap' }}>
                                                    {priceDisplay}
                                                </td>
                                            )}
                                            <td style={{ padding: '5px 2px', whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', border: styleSettings.shopQuantityBorder || '1px solid #cbd5e1', borderRadius: styleSettings.shopQuantityRadius || '4px', overflow: 'hidden', background: styleSettings.shopQuantityBg || '#fff', height: '26px' }}>
                                                    <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(product.id, currentQty - 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', color: styleSettings.shopQuantityBtnColor || '#475569', lineHeight: 1, userSelect: 'none' }}>−</button>
                                                    <input type="number" min="1" value={currentQty} onChange={e => handleTableQtyChange && handleTableQtyChange(product.id, e.target.value)} style={{ width: '30px', height: '100%', border: 'none', textAlign: 'center', fontWeight: '700', fontSize: '0.8rem', outline: 'none', margin: 0, padding: 0, color: styleSettings.shopQuantityTextColor || '#0f172a', background: 'transparent', boxSizing: 'border-box' }} />
                                                    <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(product.id, currentQty + 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', color: styleSettings.shopQuantityBtnColor || '#475569', lineHeight: 1, userSelect: 'none' }}>+</button>
                                                </div>
                                            </td>
                                            <td style={{ padding: '5px 2px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button type="button" onClick={() => handleTableAddToCart && handleTableAddToCart(product)} style={{ padding: '4px 8px', background: isAdded ? '#16a34a' : (styleSettings.primaryColor || primaryColor), color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}>
                                                    {isAdded ? '✓' : '🛒'}
                                                </button>
                                            </td>
                                        </tr>
                                        {hasSelectableOptions && isExpanded && (
                                            <tr style={{ background: '#f0f9ff', borderBottom: '2px solid #93c5fd' }}>
                                                <td colSpan={7} style={{ padding: '8px 12px 10px 48px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', background: '#ffffff', padding: '10px 14px', borderRadius: '8px', border: '1px solid #bfdbfe', boxShadow: '0 2px 5px rgba(37,99,235,0.08)' }}>
                                                        <span style={{ fontSize: '0.76rem', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                                            ⚙️ Selectable Options:
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', flex: 1 }}>
                                                            {Object.entries(selectableAttrs).map(([attrName, optionsList]) => (
                                                                <div key={attrName} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }}>
                                                                    <span style={{ fontWeight: '700', color: '#334155' }}>{attrName}:</span>
                                                                    <select
                                                                        value={effectiveOptions[attrName] || optionsList[0]}
                                                                        onChange={(e) => handleOptionChange && handleOptionChange(product.id, attrName, e.target.value)}
                                                                        style={{
                                                                            padding: '4px 10px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #3b82f6',
                                                                            fontSize: '0.8rem',
                                                                            background: '#ffffff',
                                                                            color: '#0f172a',
                                                                            fontWeight: '700',
                                                                            cursor: 'pointer',
                                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                                                        }}
                                                                    >
                                                                        {optionsList.map(opt => (
                                                                            <option key={opt} value={opt}>{opt}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setQuickOrderModalProduct && setQuickOrderModalProduct(product)}
                                                            style={{
                                                                background: '#eff6ff',
                                                                color: '#2563eb',
                                                                border: '1px solid #bfdbfe',
                                                                borderRadius: '6px',
                                                                padding: '4px 10px',
                                                                fontSize: '0.74rem',
                                                                fontWeight: '700',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            <span>⚙️ Full Configurator Modal</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards for Table view */}
                <div className="quick-order-mobile-cards" style={{ display: 'none', flexDirection: 'column', gap: '10px' }}>
                    {qoPagedProducts.map((product, idx) => {
                        const selectableAttrs = getSelectableAttributes ? getSelectableAttributes(product) : {};
                        const effectiveOptions = getEffectiveSelectedOptions ? getEffectiveSelectedOptions(product) : {};
                        const matchedVar = getMatchedVariation ? getMatchedVariation(product, effectiveOptions) : null;
                        const sku = matchedVar?.sku || product.sku || product.pageConfig?.sku || product.id || 'N/A';
                        const imageSrc = matchedVar?.image ? resolveMediaUrl(matchedVar.image) : getProductImageUrl(product);
                        const hasSelectableOptions = Object.keys(selectableAttrs).length > 0;
                        const isExpanded = !!expandedRowIds[product.id];

                        const mobileVarPriceInfo = getVariationPriceDetails(matchedVar, product);
                        const priceDisplay = (effectiveCommercialMode || (product.inquiryOnly && hideCallForPrice))
                            ? null
                            : (product.inquiryOnly
                                ? <span style={{ color: styleSettings.shopCallForPriceColor || '#d97706', fontWeight: 'bold', fontSize: callForPriceFontSize }}>{callForPriceText}</span>
                                : <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.95rem' }}>
                                    {hasSelectableOptions && matchedVar ? (
                                        mobileVarPriceInfo.isOnSale && mobileVarPriceInfo.regularPrice ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px' }}>
                                                <span>{activeFormatPrice ? activeFormatPrice(mobileVarPriceInfo.price) : `$${mobileVarPriceInfo.price.toFixed(2)}`}</span>
                                                <span style={{ fontSize: '0.82em', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '500' }}>
                                                    {activeFormatPrice ? activeFormatPrice(mobileVarPriceInfo.regularPrice) : `$${mobileVarPriceInfo.regularPrice.toFixed(2)}`}
                                                </span>
                                            </span>
                                        ) : mobileVarPriceInfo.price > 0 ? (
                                            activeFormatPrice ? activeFormatPrice(mobileVarPriceInfo.price) : `$${mobileVarPriceInfo.price.toFixed(2)}`
                                        ) : (
                                            getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                        )
                                    ) : (
                                        getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                    )}
                                </span>);
                        const specSheetUrl = product.specSheetUrl || product.pageConfig?.specSheetUrl;
                        const resolvedSpecUrl = specSheetUrl ? resolveMediaUrl(specSheetUrl) : null;
                        const currentQty = tableQuantities[product.id] ?? 0;
                        const isAdded = !!addedRowIds[product.id];

                        return (
                            <div key={product.id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <img src={imageSrc} alt={product.name} loading={idx < 4 ? "eager" : "lazy"} width="44" height="44" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer', flexShrink: 0 }} onClick={() => navigate(normalizeAppUrl(`/product/${product.slug || product.id}`))} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.88rem', cursor: 'pointer', lineHeight: '1.25', wordBreak: 'break-word' }} onClick={() => navigate(normalizeAppUrl(`/product/${product.slug || product.id}`))}>{product.name}</div>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
                                            <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '0.74rem', background: '#f1f5f9', color: '#475569', padding: '1px 5px', borderRadius: '3px' }}>SKU: {sku}</span>
                                            {resolvedSpecUrl && (<a href={resolvedSpecUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: '600', textDecoration: 'none', background: '#eff6ff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #bfdbfe' }}>📄 PDF Spec</a>)}
                                            {hasSelectableOptions && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRowExpand && toggleRowExpand(product.id)}
                                                    style={{
                                                        background: isExpanded ? '#3b82f6' : '#f1f5f9',
                                                        color: isExpanded ? '#ffffff' : '#475569',
                                                        border: isExpanded ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                                        borderRadius: '10px',
                                                        padding: '1px 6px',
                                                        fontSize: '0.68rem',
                                                        fontWeight: '700',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    ⚙️ Options ({Object.keys(selectableAttrs).length}) {isExpanded ? '▲' : '▼'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {hasSelectableOptions && isExpanded && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: '#eff6ff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                                        {Object.entries(selectableAttrs).map(([attrName, optionsList]) => (
                                            <div key={attrName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', fontSize: '0.78rem' }}>
                                                <span style={{ fontWeight: '700', color: '#1e3a8a' }}>{attrName}:</span>
                                                <select
                                                    value={effectiveOptions[attrName] || optionsList[0]}
                                                    onChange={(e) => handleOptionChange && handleOptionChange(product.id, attrName, e.target.value)}
                                                    style={{
                                                        padding: '3px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #3b82f6',
                                                        fontSize: '0.78rem',
                                                        background: '#ffffff',
                                                        color: '#0f172a',
                                                        fontWeight: '700',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {optionsList.map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setQuickOrderModalProduct && setQuickOrderModalProduct(product)}
                                            style={{
                                                alignSelf: 'flex-start',
                                                background: '#ffffff',
                                                color: '#2563eb',
                                                border: '1px solid #bfdbfe',
                                                borderRadius: '4px',
                                                padding: '3px 8px',
                                                fontSize: '0.72rem',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                marginTop: '2px'
                                            }}
                                        >
                                            <span>⚙️ Configurator Modal</span>
                                        </button>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed #e2e8f0', paddingTop: '8px', marginTop: '2px', gap: '8px' }}>
                                    <div>{priceDisplay}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', border: styleSettings.shopQuantityBorder || '1px solid #cbd5e1', borderRadius: styleSettings.shopQuantityRadius || '4px', overflow: 'hidden', background: styleSettings.shopQuantityBg || '#fff', height: '28px' }}>
                                            <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(product.id, currentQty - 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', color: styleSettings.shopQuantityBtnColor || '#475569', lineHeight: 1, userSelect: 'none' }}>−</button>
                                            <input type="number" min="1" value={currentQty} onChange={e => handleTableQtyChange && handleTableQtyChange(product.id, e.target.value)} style={{ width: '34px', height: '100%', border: 'none', textAlign: 'center', fontWeight: 'bold', fontSize: '0.85rem', outline: 'none', margin: 0, padding: 0, color: styleSettings.shopQuantityTextColor || '#0f172a', background: 'transparent', boxSizing: 'border-box' }} />
                                            <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(product.id, currentQty + 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', color: styleSettings.shopQuantityBtnColor || '#475569', lineHeight: 1, userSelect: 'none' }}>+</button>
                                        </div>
                                        <button type="button" onClick={() => handleTableAddToCart && handleTableAddToCart(product)} style={{ padding: '6px 12px', background: isAdded ? '#16a34a' : (styleSettings.primaryColor || primaryColor), color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            {isAdded ? '✓ Added' : '🛒 Add'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {qoTotalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '14px', marginTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                        {renderPagination(qoSafePage, qoTotalPages, handleQuickOrderPageChange, sortedProducts.length, QUICK_ORDER_PAGE_SIZE, false)}
                    </div>
                )}

                {/* Quick Order Modal */}
                {quickOrderModalProduct && (() => {
                    const modalProduct = quickOrderModalProduct;
                    const modalSelectableAttrs = getSelectableAttributes ? getSelectableAttributes(modalProduct) : {};
                    const modalEffectiveOptions = getEffectiveSelectedOptions ? getEffectiveSelectedOptions(modalProduct) : {};
                    const modalMatchedVar = getMatchedVariation ? getMatchedVariation(modalProduct, modalEffectiveOptions) : null;
                    const modalPriceInfo = getVariationPriceDetails(modalMatchedVar, modalProduct);
                    const modalPrice = modalPriceInfo.price > 0 ? modalPriceInfo.price : modalProduct.price;
                    const modalSku = modalMatchedVar?.sku || modalProduct.sku || modalProduct.pageConfig?.sku || modalProduct.id || 'N/A';
                    const modalImage = modalMatchedVar?.image ? resolveMediaUrl(modalMatchedVar.image) : getProductImageUrl(modalProduct);
                    const modalQty = tableQuantities[modalProduct.id] || 1;

                    return (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(15, 23, 42, 0.65)',
                                backdropFilter: 'blur(4px)',
                                zIndex: 99999,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '20px',
                                boxSizing: 'border-box'
                            }}
                            onClick={() => setQuickOrderModalProduct(null)}
                        >
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    maxWidth: '520px',
                                    width: '100%',
                                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                    padding: '24px',
                                    boxSizing: 'border-box',
                                    position: 'relative',
                                    maxHeight: '90vh',
                                    overflowY: 'auto'
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                <button
                                    type="button"
                                    onClick={() => setQuickOrderModalProduct(null)}
                                    style={{
                                        position: 'absolute',
                                        top: '16px',
                                        right: '16px',
                                        background: '#f1f5f9',
                                        border: 'none',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        fontSize: '1.2rem',
                                        color: '#64748b',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    ✕
                                </button>

                                <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', alignItems: 'center' }}>
                                    <img
                                        src={modalImage}
                                        alt={modalProduct.name}
                                        style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                    />
                                    <div>
                                        <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#3b82f6', fontWeight: 'bold' }}>
                                            Quick Order Configurator
                                        </span>
                                        <h3 style={{ margin: '2px 0 4px 0', fontSize: '1.15rem', color: '#0f172a', fontWeight: '800' }}>
                                            {modalProduct.name}
                                        </h3>
                                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontFamily: 'monospace' }}>
                                            SKU: <strong style={{ color: '#334155' }}>{modalSku}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    {Object.keys(modalSelectableAttrs).length > 0 ? (
                                        Object.entries(modalSelectableAttrs).map(([attrName, optionsList]) => (
                                            <div key={attrName} style={{ marginBottom: '14px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                                    Select {attrName}:
                                                </label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {optionsList.map(opt => {
                                                        const isSelected = modalEffectiveOptions[attrName] === opt;
                                                        return (
                                                            <button
                                                                key={opt}
                                                                type="button"
                                                                onClick={() => handleOptionChange && handleOptionChange(modalProduct.id, attrName, opt)}
                                                                style={{
                                                                    padding: '7px 14px',
                                                                    borderRadius: '6px',
                                                                    border: isSelected ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                                    background: isSelected ? '#eff6ff' : '#ffffff',
                                                                    color: isSelected ? '#1d4ed8' : '#334155',
                                                                    fontWeight: isSelected ? 'bold' : '600',
                                                                    fontSize: '0.84rem',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                            >
                                                                {isSelected ? '✓ ' : ''}{opt}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                                            No additional customizable options for this product.
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                                    <div>
                                        <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' }}>Unit Price</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a' }}>
                                            {modalProduct.inquiryOnly ? (
                                                hideCallForPrice ? null : <span style={{ color: styleSettings.shopCallForPriceColor || '#d97706', fontSize: callForPriceFontSize }}>{callForPriceText}</span>
                                            ) : (
                                                effectiveCommercialMode ? null : (formatPrice ? formatPrice(modalPrice || 0) : `$${modalPrice}`)
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', border: styleSettings.shopQuantityBorder || '1px solid #cbd5e1', borderRadius: styleSettings.shopQuantityRadius || '6px', overflow: 'hidden', background: styleSettings.shopQuantityBg || '#fff', height: '36px' }}>
                                            <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(modalProduct.id, modalQty - 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', color: styleSettings.shopQuantityBtnColor || '#475569', userSelect: 'none' }}>−</button>
                                            <input type="number" min="1" value={modalQty} onChange={e => handleTableQtyChange && handleTableQtyChange(modalProduct.id, e.target.value)} style={{ width: '40px', height: '100%', border: 'none', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem', outline: 'none', margin: 0, padding: 0, color: styleSettings.shopQuantityTextColor || '#0f172a', background: 'transparent' }} />
                                            <button type="button" onClick={() => handleTableQtyChange && handleTableQtyChange(modalProduct.id, modalQty + 1)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '100%', padding: 0, border: 'none', background: styleSettings.shopQuantityBtnBg || '#f1f5f9', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', color: styleSettings.shopQuantityBtnColor || '#475569', userSelect: 'none' }}>+</button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (handleTableAddToCart) handleTableAddToCart(modalProduct, modalQty, modalEffectiveOptions);
                                                setQuickOrderModalProduct(null);
                                            }}
                                            style={{
                                                background: '#166534',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '10px 18px',
                                                borderRadius: '6px',
                                                fontWeight: 'bold',
                                                fontSize: '0.9rem',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(22, 101, 52, 0.25)',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <span>🛒 Confirm &amp; Add to Cart</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }

    // Standard Grid / List View
    const gridTotalPages = Math.max(1, Math.ceil(sortedProducts.length / GRID_PAGE_SIZE));
    const gridSafePage = Math.min(currentPage, gridTotalPages);
    const gridStart = (gridSafePage - 1) * GRID_PAGE_SIZE;
    const pagedGridProducts = sortedProducts.slice(gridStart, gridStart + GRID_PAGE_SIZE);

    const deskCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridColumns) || 3);
    const tabCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridTabletColumns) || Math.min(deskCols, 2));
    const mobCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridMobileColumns) || 1);
    const currentCols = isMobile ? mobCols : (isTablet ? tabCols : deskCols);
    const currentGap = isMobile
        ? addUnit(styleSettings.shopGridMobileGap || '16px')
        : (isTablet ? addUnit(styleSettings.shopGridTabletGap || styleSettings.shopGridMobileGap || '20px') : addUnit(styleSettings.shopGridGap || '30px'));

    return (
        <>
            <div
                className="shop-products-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${currentCols}, minmax(0, 1fr))`,
                    gap: currentGap,
                    width: '100%'
                }}
            >
                {pagedGridProducts.map((product, idx) => (
                    <ShopProductCard
                        key={product.id}
                        product={product}
                        idx={idx}
                        isEditing={isEditing}
                        isMobile={isMobile}
                        catalogViewMode={catalogViewMode}
                        styleSettings={styleSettings}
                        settings={settings}
                        typography={typography}
                        primaryColor={primaryColor}
                        tableQuantities={tableQuantities}
                        handleTableQtyChange={handleTableQtyChange}
                        addToCart={addToCart}
                        updateProductField={updateProductField}
                        handleDuplicateProductLive={handleDuplicateProductLive}
                        handleDeleteProductLive={handleDeleteProductLive}
                        onEditProductDetails={onEditProductDetails}
                        update={update}
                        _getFontStyles={resolveFontStyles}
                        dbCategories={dbCategories}
                        setActiveCategory={setActiveCategory}
                        setActiveSubCategory={setActiveSubCategory}
                        isCommercialMode={isCommercialMode}
                        isSimpleMode={isSimpleMode}
                        selectedSimpleSection={selectedSimpleSection}
                        onSectionSelect={onSectionSelect}
                        getSelectableAttributes={getSelectableAttributes}
                        getEffectiveSelectedOptions={getEffectiveSelectedOptions}
                        getMatchedVariation={getMatchedVariation}
                        handleOptionChange={handleOptionChange}
                        tableSelectedOptions={tableSelectedOptions}
                        formatPrice={activeFormatPrice}
                    />
                ))}
            </div>

            {gridTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '36px', paddingBottom: '20px', width: '100%' }}>
                    {renderPagination(gridSafePage, gridTotalPages, handlePageChange, sortedProducts.length, GRID_PAGE_SIZE, false)}
                </div>
            )}
        </>
    );
};

export default ShopProductGrid;
